import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import en from '../locales/en.json';
import he from '../locales/he.json';

const LOCALE_STORAGE_KEY = 'codevoyage.locale';
export const AUTO_LOCALE = 'auto';
const supportedLocales = { en, he };
const LocaleContext = createContext(null);

export function normalizeLocale(value) {
  return String(value || '').toLowerCase().startsWith('he') ? 'he' : 'en';
}

export function resolveDeviceLocale() {
  return normalizeLocale(navigator.languages?.[0] || navigator.language);
}

export function resolveInitialPreference() {
  try {
    const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
    if (saved === AUTO_LOCALE) return AUTO_LOCALE;
    if (saved && supportedLocales[normalizeLocale(saved)]) return normalizeLocale(saved);
  } catch (_) {}
  return AUTO_LOCALE;
}

export function resolveInitialLocale() {
  const preference = resolveInitialPreference();
  return preference === AUTO_LOCALE ? resolveDeviceLocale() : preference;
}

function valueAtPath(dictionary, path) {
  return path.split('.').reduce((value, part) => value && value[part], dictionary);
}

function interpolate(template, values) {
  return String(template).replace(/\{([^}]+)\}/g, (_, key) => values[key] ?? `{${key}}`);
}

export function LocaleProvider({ children }) {
  const [preference, setPreference] = useState(resolveInitialPreference);
  const locale = preference === AUTO_LOCALE ? resolveDeviceLocale() : preference;
  const setLocale = (nextLocale) => {
    const normalized = nextLocale === AUTO_LOCALE ? AUTO_LOCALE : normalizeLocale(nextLocale);
    try { window.localStorage.setItem(LOCALE_STORAGE_KEY, normalized); } catch (_) {}
    setPreference(normalized);
  };

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === 'he' ? 'rtl' : 'ltr';
  }, [locale]);

  const value = useMemo(() => ({
    locale,
    preference,
    setLocale,
    t: (key, values = {}) => interpolate(valueAtPath(supportedLocales[locale], key) || valueAtPath(en, key) || key, values),
    dateLocale: locale === 'he' ? 'he-IL' : 'en-US'
  }), [locale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale() {
  const context = useContext(LocaleContext);
  if (!context) throw new Error('useLocale must be used inside LocaleProvider');
  return context;
}
